import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { DropdownOption } from '@storybook/components/dropdown/dropdown.component';
import { MatDialog } from '@angular/material/dialog';
import { FallbackOptionsDialog } from '@pages/checkout-app/components/dialogs/fallback-option/fallback-options.dialog';
import { Shop } from '@app/services/shops/shop.model';
import { initShopCurrency } from '@pages/checkout-app/utils';
import { Shippings } from '@pages/checkout-app/types';
import { CheckoutActions } from '@pages/checkout-app/checkout-store/actions';
import { Store } from '@ngrx/store';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { takeUntil } from 'rxjs/operators';
import { ofType, Actions } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { ToastService } from '@storybook/services/toast/toast.service';
import { PdqToastComponent } from '@storybook/components/toast/toast.component';
import { UntypedFormControl } from '@angular/forms';
import { currencyStrToSign } from '@utils/string-utils';

enum ACTIONS {
  CREATE = 'create',
  EDIT = 'edit',
}

const FALLBACK_METHOD_NAME = 'Standard shipping';

@Component({
  selector: 'fallback-option',
  templateUrl: './fallback-option.component.html',
  styleUrls: ['./fallback-option.component.scss'],
  standalone: false,
})
export class FallbackOptionComponent implements OnInit, OnDestroy {
  isCheckoutEcommerceEnabled$ = this.store.select(fromCheckoutReducer.checkoutEcommerceEnabled);
  @Input() shop: Shop;
  @Input() loadedFallbackMethod: Shippings;
  enabled;
  rate;
  currency;
  ratePerDeliveryText: string = '';
  name = FALLBACK_METHOD_NAME;

  actionOptions: DropdownOption[] = [{ label: 'Edit rate', value: 'edit' }];
  disableActions: boolean = true;
  active: boolean = false;
  disableShippingAddress = new UntypedFormControl(false);

  destroyed$ = new Subject();

  constructor(
    private dialog: MatDialog,
    private store: Store<fromCheckoutReducer.State>,
    private enableChange$: Actions,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.detectEnableChange();
    this.currency = initShopCurrency(this.shop);
    this.disableShippingAddress.setValue(
      this.loadedFallbackMethod?.metadata?.disableShippingAddressConditionCheck ?? null,
    );
    if (this.loadedFallbackMethod?.enabled) {
      this.rate = this.loadedFallbackMethod.rate;
      this.active = this.loadedFallbackMethod.enabled;
      this.disableActions = false;
      this.name = this.loadedFallbackMethod.name;
    }

    this.ratePerDeliveryText =
      this.rate != '' && this.rate ? `Fallback rate per delivery ${currencyStrToSign(this.currency)}${this.rate}` : '';
  }

  detectEnableChange(): void {
    this.enableChange$
      .pipe(
        takeUntil(this.destroyed$),
        ofType(CheckoutActions.editCheckoutSuccess, CheckoutActions.addNewShippingsCheckoutSuccess),
      )
      .subscribe((res) => {
        const { shippings, type } = res;
        if (type?.includes('Add new') && shippings.fallbackMethod) {
          this.loadedFallbackMethod = shippings;
        }
        this.toastService.show(PdqToastComponent, {
          message: `Fallback rate is ${this.active ? 'on' : 'off'}`,
          mode: 'info',
        });
      });
  }

  onAction({ value }: any) {
    if (value == ACTIONS.EDIT) {
      const editRef = this.dialog.open(FallbackOptionsDialog, {
        panelClass: 'fallback-options-dialog-class',
        data: { mode: 'on', currency: this.currency, action: ACTIONS.EDIT, rate: this.rate, name: this.name },
      });

      editRef.componentInstance.onAction.subscribe((event) => {
        this.rate = event && event.rate;
        this.name = event && event.name;
        // on edit
        this.handleEditFallbackMethod();
        editRef.close();
      });
    }
  }

  onToggleEnable(mode?) {
    let nextMode;
    this.active ? (nextMode = 'off') : (nextMode = 'on');

    const dialogRef = this.dialog.open(FallbackOptionsDialog, {
      panelClass: 'fallback-options-dialog-class',
      data: { mode: nextMode, currency: this.currency, action: ACTIONS.CREATE },
    });

    const subscription$ = dialogRef.componentInstance.onAction.subscribe((event) => {
      if (event.mode == 'on') {
        this.handleEnableFallbackMethod(event && event.rate, event && event.name);
      }
      if (event.mode == 'off') {
        this.handleDisableFallbackMethod();
      }

      dialogRef.close();
    });

    dialogRef.afterClosed().subscribe(() => {
      subscription$.unsubscribe();
    });
  }

  handleEnableFallbackMethod(rate: any, name: string): void {
    this.active = true;
    this.disableActions = false;
    this.rate = rate;
    this.name = name;

    if (this.loadedFallbackMethod) {
      this.handleEditFallbackMethod();
    } else {
      this.handleNewFallbackMethod();
    }
  }

  handleDisableFallbackMethod(): void {
    this.active = false;
    this.disableActions = true;
    this.rate = '';
    this.name = FALLBACK_METHOD_NAME;
    this.handleEditFallbackMethod();
  }

  handleNewFallbackMethod(): void {
    const shippings: Shippings = {
      rate: this.rate,
      enabled: this.active,
      fallbackMethod: true,
      name: this.name,
    };
    this.store.dispatch(CheckoutActions.addNewShippingsCheckout({ shippings }));
    this.ratePerDeliveryText =
      this.rate != '' && this.rate ? `Fallback rate per delivery ${currencyStrToSign(this.currency)}${this.rate}` : '';
  }

  handleEditFallbackMethod(): void {
    const shippingsId = this.loadedFallbackMethod?.id;
    const shippings: Shippings = {
      rate: this.rate,
      enabled: this.active,
      name: this.name,
    };
    this.store.dispatch(CheckoutActions.editCheckout({ shippings, shippingsId }));
    this.ratePerDeliveryText =
      this.rate != '' && this.rate ? `Fallback rate per delivery ${currencyStrToSign(this.currency)}${this.rate}` : '';
  }

  onDisableShippingAddressCondition(event) {
    const metadata = { ...this.loadedFallbackMethod?.metadata, disableShippingAddressConditionCheck: event };

    if (typeof metadata.disableShippingAddressConditionCheck !== 'boolean') {
      const shippings: Shippings = {
        metadata,
        fallbackMethod: true,
        name: this.name,
      };
      this.store.dispatch(CheckoutActions.addNewShippingsCheckout({ shippings }));
    } else {
      const shippingsId = this.loadedFallbackMethod?.id;
      const shippings: Shippings = {
        metadata,
        name: this.name,
      };
      this.store.dispatch(CheckoutActions.editCheckout({ shippings, shippingsId }));
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
